import { useEffect, useState } from "react";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Disclaimer, Section, Spinner } from "@/components/kit";
import { api, brl, brlShort, pct } from "@/lib/api";
import { useBiz, useBizGet } from "./shared";
import { TT } from "./Overview";

const P = [["clients", "Clientes / pacientes por mês"], ["avg_price", "Preço médio (R$)"], ["volume_per_client", "Vendas por cliente"], ["var_cost_pct", "Custos variáveis (% da receita líquida)"],
  ["fixed_costs", "Despesas fixas (R$/mês)"], ["employees", "Funcionários adicionais"], ["cost_per_employee", "Custo por funcionário (R$/mês)"], ["investment", "Investimento planejado (R$)"],
  ["tax_pct", "Impostos sobre vendas (%)"], ["income_tax_pct", "Imposto sobre o lucro (%)"], ["default_pct", "Inadimplência (%)"], ["growth_pct", "Crescimento mensal previsto (%)"],
  ["debt_installments", "Parcelas de dívidas (R$/mês)"], ["receive_days", "Prazo médio de recebimento (dias)"], ["pay_days", "Prazo médio de pagamento (dias)"], ["opening_cash", "Caixa inicial (R$)"]];
const COLORS = { conservador: "#EF4444", base: "#D4AF37", otimista: "#10B981" };

export function Simulator() {
  const { cid } = useBiz();
  const { data: base } = useBizGet("/simulator/baseline");
  const [p, setP] = useState(null);
  const [res, setRes] = useState(null);
  useEffect(() => { if (base) { setP(base); } }, [base]);
  useEffect(() => {
    if (!p) { return; }
    const t = setTimeout(() => {
      const body = Object.fromEntries(P.map(([k]) => [k, Number(p[k] || 0)]));
      api.post(`/biz/companies/${cid}/simulator`, body).then((r) => setRes(r.data)).catch(() => setRes(null));
    }, 350);
    return () => { clearTimeout(t); };
  }, [p, cid]);
  if (!p) return <Spinner />;
  const chart = res ? res.base.pontos.map((x, i) => ({ mes: `M${x.mes}`, ...Object.fromEntries(Object.keys(res).map((k) => [k, res[k].pontos[i].caixa])) })) : [];
  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
      <Section title="Parâmetros" testid="biz-sim-params">
        <p className="mb-3 text-xs text-slate-400">{base?.source ? `Valores iniciais copiados de ${base.source}.` : "Sem dados lançados: preencha os parâmetros."} A simulação nunca altera seus lançamentos reais.</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-1">
          {P.map(([k, l]) => (
            <label key={k} className="text-xs text-slate-400">{l}
              <input type="number" step="any" value={p[k] ?? ""} onChange={(e) => setP({ ...p, [k]: e.target.value })} data-testid={`biz-sim-${k}`}
                className="mt-1 block h-9 w-full rounded-md border border-[#1E3A6E] bg-[#0B1A33] px-2 text-sm text-white" />
            </label>
          ))}
        </div>
      </Section>
      <div className="space-y-6 xl:col-span-2">
        {res && <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {Object.entries(res).map(([k, s]) => (
            <div key={k} className="card-p p-5" style={{ borderTop: `3px solid ${COLORS[k]}` }} data-testid={`biz-sim-${k}-card`}>
              <div className="text-sm font-semibold capitalize">{k}</div>
              <div className="mb-3 text-[11px] text-slate-500">{s.premissas}</div>
              {[["Faturamento (mês 1)", brl(s.faturamento_mes1)], ["Margem de contribuição", `${brl(s.margem_mes1)} (${s.margem_pct == null ? "—" : pct(s.margem_pct)})`], ["EBITDA (mês 1)", brl(s.ebitda_mes1)],
                ["Lucro (mês 1)", brl(s.lucro_mes1)], ["Lucro em 12 meses", brl(s.lucro_12m)], ["Ponto de equilíbrio", s.ponto_equilibrio == null ? "Margem ≤ 0" : brl(s.ponto_equilibrio)],
                ["Caixa em 12 meses", brl(s.caixa_final)], ["Menor caixa no período", brl(s.menor_caixa)], ["Capital de giro necessário", brl(s.capital_giro_necessario)]].map(([l, v]) => (
                <div key={l} className="flex justify-between gap-2 py-0.5 text-xs"><span className="text-slate-400">{l}</span><span className={`num ${String(v).includes("-") ? "text-red-300" : ""}`}>{v}</span></div>
              ))}
            </div>
          ))}
        </div>}
        <Section title="Caixa projetado — 12 meses" testid="biz-sim-chart">
          <div className="h-72"><ResponsiveContainer><LineChart data={chart}>
            <CartesianGrid stroke="#1E3A6E" strokeDasharray="3 3" /><XAxis dataKey="mes" stroke="#64748B" fontSize={11} /><YAxis stroke="#64748B" fontSize={11} tickFormatter={brlShort} width={70} />
            <Tooltip {...TT} /><Legend />
            {Object.keys(COLORS).map((k) => <Line key={k} dataKey={k} name={k} stroke={COLORS[k]} strokeWidth={2} dot={false} />)}
          </LineChart></ResponsiveContainer></div>
        </Section>
        <Disclaimer text="Simulação hipotética com premissas explícitas; resultados reais dependem da execução e do mercado." />
      </div>
    </div>
  );
}
